'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { SearchApi } from '@ps/api/search';
import {
  OrderEnumItem,
  type PostSearchItem,
  PostStatusEnumItem,
} from '@ps/api/types';
import { withClientAPI } from '@ps/functions/utils/api';

interface State {
  posts: PostSearchItem[];
  pageNo: number;
  isInitialLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: Error | undefined;
}

const kInitialState: State = {
  posts: [],
  pageNo: 0,
  isInitialLoading: true,
  isLoadingMore: false,
  hasMore: true,
  error: undefined,
};

const kPageSize = 10;

interface UseCreatorPostsPaginationResult {
  state: State;
  loadMore: () => void;
  refresh: () => void;
}

/**
 * Paginated list of a creator's published posts — the data source
 * for the post-picker modal. `loadMore` appends subsequent pages;
 * newest-first ordering matches the PopshopRN picker.
 *
 * Gated on `storeId` AND `enabled`: nothing is fetched until BOTH a
 * store id exists and the picker is actually open. The dialog is
 * always mounted (just `open={false}`), so without the `enabled` gate
 * this would eagerly load the creator's posts on every channel view —
 * pointless work for the owner (before they open the picker) and for
 * an audience member (who never opens it). Re-enabling (re-opening)
 * re-fetches page 1.
 */
export function useCreatorPostsPagination(
  storeId: string | null | undefined,
  enabled: boolean,
): UseCreatorPostsPaginationResult {
  const [state, setState] = useState<State>(kInitialState);
  // Carry the in-flight fetch's request key on a ref so callbacks
  // can detect "did the store id change while I was fetching?" and
  // drop late results.
  const requestKeyRef = useRef<string | undefined>(undefined);

  const fetchPage = useCallback(
    async (pageNo: number, reset: boolean) => {
      if (!storeId) return;
      const requestKey = `${storeId}#${pageNo}#${reset ? 'reset' : 'append'}`;
      requestKeyRef.current = requestKey;
      try {
        const api = withClientAPI(SearchApi);
        const res = await api.searchApiControllerSearchPosts({
          storeId,
          pageNo,
          pageSize: kPageSize,
          status: [PostStatusEnumItem.Published],
          order: OrderEnumItem.Desc,
        });
        if (requestKeyRef.current !== requestKey) return;
        const rows = res.data.rows;
        setState(prev => {
          const merged = reset ? rows : dedupePosts([...prev.posts, ...rows]);
          return {
            posts: merged,
            pageNo,
            isInitialLoading: false,
            isLoadingMore: false,
            hasMore: rows.length >= kPageSize,
            error: undefined,
          };
        });
      } catch (error: unknown) {
        if (requestKeyRef.current !== requestKey) return;
        setState(prev => ({
          ...prev,
          isInitialLoading: false,
          isLoadingMore: false,
          error: error instanceof Error ? error : new Error(String(error)),
        }));
      }
    },
    [storeId],
  );

  // Fetch page 1 only once the picker is open (and re-fetch on re-open or a
  // storeId change). Idle — no request — while closed or storeId-less.
  useEffect(() => {
    if (!storeId || !enabled) {
      setState(kInitialState);
      return;
    }
    setState({
      ...kInitialState,
      isInitialLoading: true,
    });
    void fetchPage(1, true);
  }, [storeId, enabled, fetchPage]);

  const loadMore = useCallback(() => {
    setState(prev => {
      if (prev.isInitialLoading || prev.isLoadingMore || !prev.hasMore) {
        return prev;
      }
      void fetchPage(prev.pageNo + 1, false);
      return { ...prev, isLoadingMore: true, error: undefined };
    });
  }, [fetchPage]);

  const refresh = useCallback(() => {
    void fetchPage(1, true);
  }, [fetchPage]);

  return { state, loadMore, refresh };
}

function dedupePosts(posts: PostSearchItem[]): PostSearchItem[] {
  const seen = new Set<string>();
  const out: PostSearchItem[] = [];
  for (const post of posts) {
    if (seen.has(post.id)) continue;
    seen.add(post.id);
    out.push(post);
  }
  return out;
}
